import cn from 'classnames';
import { forwardRef } from 'react';

import { Tag } from '../tag';

import type { ForwardedRef, ReactElement } from 'react';

import type { AssetTagProps } from './asset-tag.types';

import './asset-tag.scss';

/**
 * The purpose of this component is the visual differentiation of a particular element
 * on the screen (for example table row). Users can set a custom color for each custom tag
 * to visually distinguish it from the rest, for better organisation.
 *
 * ### Usage
 *
 * ```tsx
 * import { AssetTag } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return <AssetTag>Banana</AssetTag>;
 * }
 * ```
 */
const AssetTag = forwardRef((props: AssetTagProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
  const {
    children,
    className,
    dismissible = false,
    variant = 'default',
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;

  const classes = cn('rbui-asset-tag', variant !== 'default' ? `rbui-asset-tag--${variant}` : null, className);

  return (
    <Tag
      className={classes}
      ref={forwardedRef}
      {...rest}
      bordered={false}
      disabled={false}
      dismissible={dismissible}
      readOnly
    >
      {children}
    </Tag>
  );
});

AssetTag.displayName = 'AssetTag';

export default AssetTag;
